import React, { useEffect, useState } from "react";
import { X, AlertTriangle, AlertCircle, Loader2, User, Hash, CalendarCheck, ShieldAlert, Target } from "lucide-react";
import { apiClient } from "../../../lib/api";

// INTERFACES
export interface DenuncianteApi {
  id: number;
  nombre: string;
}

export interface ReferenciaContextoApi {
  bookingId: number | null;
  listingRentId: number | null;
}

export interface ComplaintDetailApi {
  idDenuncia: number;
  motivo: string;
  puntaje: number;
  severityLevel: string;
  descripcion: string | null;
  fechaCreacion: string;
  estado: string;
  denunciante: DenuncianteApi;
  rolDenunciante: string;
  rolDenunciado: string;
  referenciaContexto: ReferenciaContextoApi;
}

export interface ComplaintResponse {
  data: ComplaintDetailApi[];
  statusCode: string;
  hasError: boolean;
  resultError: any | null;
}

// PROPS DEL MODAL
interface UserComplaintsModalProps {
  isOpen: boolean;
  userId: string | number | null;
  userName: string | null;
  onClose: () => void;
}

const UserComplaintsModal: React.FC<UserComplaintsModalProps> = ({ isOpen, userId, userName, onClose }) => {
  const [complaints, setComplaints] = useState<ComplaintDetailApi[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && userId !== null) {
      fetchComplaints();
    } else {
      setComplaints([]);
      setError(null);
    }
  }, [isOpen, userId]);

  const fetchComplaints = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get<ComplaintResponse>(`Complaint/ByReportedUser/${userId}`);
      setComplaints(response.data || []);
    } catch (err) {
      console.error("Error obteniendo los detalles de la denuncia:", err);
      setError("No se pudieron cargar los reportes. Inténtalo de nuevo más tarde.");
    } finally {
      setLoading(false);
    }
  };

  // FUNCIÓN PARA SUMAR PUNTAJES: Usa reduce para iterar el array y sumar la propiedad "puntaje"
  const totalScore = complaints.reduce((acumulador, denuncia) => acumulador + denuncia.puntaje, 0);

  // Helper para el color del estado
  const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case 'PENDING': return "bg-amber-50 text-amber-700 border-amber-200";
      case 'START': return "bg-blue-50 text-blue-700 border-blue-200";
      case 'RESOLVED': return "bg-emerald-50 text-emerald-700 border-emerald-200";
      default: return "bg-gray-50 text-gray-700 border-gray-200";
    }
  };

  // Helper para el color del severityLevel
  const getSeverityColor = (severity: string) => {
    switch (severity.toUpperCase()) {
      case 'ALTA': return "bg-red-100 text-red-800 border-red-300";
      case 'MEDIA': return "bg-orange-100 text-orange-800 border-orange-300";
      case 'BAJA': return "bg-yellow-100 text-yellow-800 border-yellow-300";
      default: return "bg-gray-100 text-gray-800 border-gray-300";
    }
  };

  if (!isOpen || !userId) return null;

  return (
    <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <div className="bg-white border border-gray-200 rounded-xl w-[600px] max-w-[95vw] shadow-xl flex flex-col max-h-[85vh]">

        {/* Cabecera */}
        <div className="flex justify-between items-center border-b border-gray-200 p-5 shrink-0">
          <div className="flex items-center gap-3 text-gray-900 font-bold text-[17px]">
            <div className="w-9 h-9 rounded-lg bg-red-50 border border-red-100 flex items-center justify-center text-red-500 shrink-0">
              <AlertTriangle size={18} strokeWidth={2.5} />
            </div>
            <div className="flex flex-col">
              <h2 className="text-gray-900 font-bold text-[17px] leading-tight">Historial de Denuncias</h2>
              {userName && (
                <span className="text-[13px] text-gray-500 font-medium mt-0.5">
                  Usuario: <span className="text-gray-700">{userName}</span>
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 hover:bg-gray-100 p-1.5 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Contenido */}
        <div className="p-5 overflow-y-auto bg-gray-50/50 flex-1 scrollbar-thin scrollbar-thumb-gray-300">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <Loader2 className="animate-spin text-red-500 mb-3" size={32} />
              <p className="text-sm font-medium">Cargando reportes del usuario...</p>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-10 text-red-500 text-sm font-medium text-center bg-red-50 rounded-lg border border-red-100">
              {error}
            </div>
          ) : complaints.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <AlertCircle size={40} className="mb-3 opacity-40" strokeWidth={1.5} />
              <p className="text-sm font-medium">No hay reportes detallados para mostrar.</p>
            </div>
          ) : (
            <div className="flex flex-col gap-4">

              {/* PANEL DE RESUMEN: Muestra el puntaje total acumulado */}
              <div className="bg-white border border-red-200 rounded-lg p-3 flex justify-between items-center shadow-sm">
                <div className="flex items-center gap-2 text-red-700">
                  <ShieldAlert size={18} />
                  <span className="text-[13px] font-bold uppercase tracking-wide">Puntaje de Gravedad Total</span>
                </div>
                <div className="bg-red-50 text-red-700 border border-red-200 px-3 py-1 rounded-md text-[15px] font-bold">
                  {totalScore}
                </div>
              </div>

              {/* Lista de denuncias */}
              {complaints.map((complaint) => (
                <div key={complaint.idDenuncia} className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm hover:border-red-200 transition-colors">

                  {/* Fila Superior: Motivo, Puntaje y Estado */}
                  <div className="flex justify-between items-start mb-3 gap-2">
                    <div className="flex flex-col gap-1">
                      <span className="font-bold text-gray-900 text-[15px] leading-tight">
                        {complaint.motivo}
                      </span>
                      <span className="text-[11px] text-gray-500 font-medium">
                        {new Date(complaint.fechaCreacion).toLocaleDateString('es-ES', {
                          day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
                        })}
                      </span>
                    </div>
                    <div className="flex flex-col items-end gap-1.5 shrink-0">

                      <div className="flex items-center gap-1">
                        <div className={`px-2 py-0.5 rounded text-[10px] font-bold border uppercase tracking-wide ${getSeverityColor(complaint.severityLevel)}`}>
                          Gravedad {complaint.severityLevel}
                        </div>
                        <div className={`px-2 py-0.5 rounded text-[10px] font-bold border uppercase tracking-wide ${complaint.puntaje >= 50
                          ? "bg-red-50 text-red-700 border-red-200"
                          : "bg-orange-50 text-orange-700 border-orange-200"
                          }`}>
                          +{complaint.puntaje}
                        </div>
                      </div>

                      {/* <div className={`px-2 py-0.5 rounded text-[10px] font-bold border uppercase tracking-wide ${complaint.puntaje >= 50
                          ? "bg-red-50 text-red-700 border-red-200"
                          : "bg-orange-50 text-orange-700 border-orange-200"
                        }`}>
                        Gravedad: {complaint.puntaje}
                      </div> */}
                      <div className={`px-2 py-0.5 rounded text-[9px] font-bold border uppercase tracking-wide ${getStatusColor(complaint.estado)}`}>
                        Estado: {complaint.estado}
                      </div>
                    </div>
                  </div>

                  {/* Contexto e Involucrados */}
                  <div className="flex flex-col gap-2.5 mb-3 bg-gray-50 p-3 rounded-lg border border-gray-100">

                    <div className="flex flex-wrap gap-x-5 gap-y-2">

                      {/* Quién denuncia */}
                      <div className="flex items-center gap-1.5 text-[12px] text-gray-700">
                        <span className="font-bold text-gray-400 uppercase text-[10px] tracking-wide shrink-0">De:</span>
                        <User size={14} className="text-blue-500 shrink-0" />
                        <span className="font-medium truncate max-w-[120px]" title={complaint.denunciante.nombre}>
                          {complaint.denunciante.nombre}
                        </span>
                        <span className="text-[9px] text-blue-700 font-bold bg-blue-100 px-1.5 py-0.5 border border-blue-200 rounded uppercase tracking-wider shrink-0">
                          {complaint.rolDenunciante}
                        </span>
                      </div>

                      {/* Rol bajo el que fue denunciado */}
                      <div className="flex items-center gap-1.5 text-[12px] text-gray-700">
                        <span className="font-bold text-gray-400 uppercase text-[10px] tracking-wide shrink-0">A:</span>
                        <Target size={14} className="text-red-500 shrink-0" />
                        <span className="text-[9px] text-red-700 font-bold bg-red-100 px-1.5 py-0.5 border border-red-200 rounded uppercase tracking-wider shrink-0">
                          {complaint.rolDenunciado}
                        </span>
                      </div>

                    </div>

                    {/* Separador sutil si hay contexto de reserva/inmueble */}
                    {(complaint.referenciaContexto.bookingId || complaint.referenciaContexto.listingRentId) && (
                      <div className="flex flex-wrap gap-3 pt-2 mt-0.5 border-t border-gray-200/70">
                        {complaint.referenciaContexto.bookingId && (
                          <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
                            <CalendarCheck size={13} className="text-gray-400" />
                            <span className="font-medium">Reserva #{complaint.referenciaContexto.bookingId}</span>
                          </div>
                        )}
                        {complaint.referenciaContexto.listingRentId && (
                          <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
                            <Hash size={13} className="text-gray-400" />
                            <span className="font-medium">Propiedad #{complaint.referenciaContexto.listingRentId}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Descripción */}
                  <div className="text-[13px] text-gray-700 bg-gray-50/80 p-3 rounded-lg border border-gray-100">
                    <span className="font-semibold text-gray-500 text-[11px] uppercase block mb-1">Detalle del reporte:</span>
                    {complaint.descripcion || <span className="italic text-gray-400">El usuario no proporcionó detalles adicionales.</span>}
                  </div>

                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 shrink-0 flex justify-end bg-white rounded-b-xl">
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-lg text-xs font-semibold bg-white border border-gray-300 text-gray-700 hover:text-gray-900 hover:bg-gray-50 transition-colors shadow-sm"
          >
            Cerrar panel
          </button>
        </div>

      </div>
    </div>
  );
};

export default UserComplaintsModal;